import { IDemo } from '@/src/lib/types/themeInterface';
import React, { FC } from 'react';
import { usePathname } from 'next/navigation';
import NextImage from '../nextImage/NextImage';
import imageHelper from '@/src/lib/helpers/imageHelper';
import styles from './DemoCard.module.scss';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';
import Button from '../button/Button';
import { ITheme } from '@/src/lib/types/themeInterface/index';
import { getCurrentPreviewUrl } from '@/src/lib/helpers/getThemeUrls';
import { stockistTexts, promptTexts } from './constants';

type TDemo = {
  demoData: IDemo;
  theme?: ITheme;
  handleFilters?: (activeTag: string) => void;
  index: number;
  isThemePage?: boolean;
};

const DemoCard: FC<TDemo> = ({ demoData, theme, handleFilters, index, isThemePage }) => {
  const { demoName, demoTag, demoImage } = demoData;
  const pathname = usePathname();

  const { ref, inView } = useInView({
    initialInView: false,
    rootMargin: `-180px`,
    triggerOnce: true,
  });

  const demoUrl = theme && getCurrentPreviewUrl(theme, demoData);

  return (
    theme && (
      <article
        className={classNames(styles.card, { [styles[`card--animate`]]: inView })}
        ref={ref}
        style={{ animationDelay: `${(index % 3) * 0.25}s` }}>
        <div className={styles.card__wrapper}>
          {demoImage ? (
            <div className={styles.card__img}>
              <NextImage
                src={imageHelper(demoImage).url}
                alt={demoName}
                fill
                sizes="(max-width: 480px) 90vw, (max-width: 768px) 44.3vw, 30vw"
              />
            </div>
          ) : (
            <div className={styles.backlog}></div>
          )}

          <div className={styles.hover__wrapper}>
            {!pathname.includes(`${theme.slug}`) && (
              <Button
                buttonUrl={`/themes/${theme.slug}`}
                ariaLabel={`Learn More about ${theme.title} Theme`}
                className={styles.hover__learn}
                color="primary-dark"
                action="interactive">
                Explore {theme.title}
              </Button>
            )}
            <Button
              ariaLabel="Preview Theme"
              buttonUrl={demoUrl}
              className={styles.hover__preview}
              color="primary-light"
              action="interactive"
              target>
              Preview Theme
            </Button>
          </div>
        </div>

        <div className={styles.card__info}>
          <div className={styles.info__top}>
            {isThemePage ? (
              <p className={`heading__5 ${styles.theme__name}`}>{demoName}</p>
            ) : (
              <p className={`heading__5 ${styles.theme__name}`}>{theme.title}</p>
            )}
            <span
              className={classNames(styles.demo__tag, {
                [styles[`tag__clickable`]]: !pathname.includes(`${theme.slug}`),
              })}
              onClick={() => handleFilters && handleFilters(demoTag)}>
              {demoTag}
            </span>
          </div>

          {isThemePage && theme.slug === 'stockist' && stockistTexts[demoName] && (
            <>
              <div className={styles.card__desc}>{stockistTexts[demoName]}</div>
              <a
                href={demoUrl}
                className={styles.card__button}
                target="_blank"
                rel="noopener noreferrer">
                <em>View {demoTag} Demo</em>
                <strong>
                  <svg
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.25 4L8.75 7.5L5.25 11"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <svg
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.25 4L8.75 7.5L5.25 11"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </strong>
              </a>
            </>
          )}

          {isThemePage && theme.slug === 'prompt' && promptTexts[demoName] && (
            <>
              <div className={styles.card__desc}>{promptTexts[demoName]}</div>
              <a
                href={demoUrl}
                className={styles.card__button}
                target="_blank"
                rel="noopener noreferrer">
                <em>View {demoTag} Demo</em>
                <strong>
                  <svg
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.25 4L8.75 7.5L5.25 11"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <svg
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.25 4L8.75 7.5L5.25 11"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </strong>
              </a>
            </>
          )}

          {!isThemePage && demoName !== theme.title && (
            <p className={`${styles.demo__name}`}>{demoName}</p>
          )}
        </div>
      </article>
    )
  );
};

export default DemoCard;
